import Papa from 'papaparse';

export interface CSVColumn {
  key: string;
  header: string;
  /**
   * Display label for the column reference and the import preview. Defaults to the
   * Title-Cased `header`; set it only when that reads wrong for a column. The template
   * and export files always use the snake_case `header`.
   */
  label?: string;
  /** File headers the column-mapping step also matches to this column */
  headerAliases?: string[];
  required: boolean;
  enumValues?: string[];
  isMultipleEnum?: boolean;
  exampleValue?: string;
  description?: string;
}

export interface CSVValidationError {
  row: number;
  field: string;
  message: string;
  value?: string;
}

export type CSVHeaderMap = Record<string, string>;

export interface CSVFileHeaders {
  headers: string[];
  sampleValues: Record<string, string>;
}

export interface CSVParseResult<T = Record<string, string>> {
  data: T[];
  errors: CSVValidationError[];
  validRows: T[];
  invalidRows: { row: T; errors: CSVValidationError[] }[];
}

// Acronyms that stay uppercase when CSV headers are displayed as titles
const CSV_HEADER_ACRONYMS = new Set(['id', 'po', 'uom', 'csv']);

/** Display form of a snake_case CSV header, e.g. 'customer_external_id' -> 'Customer External ID'. */
export function humanizeCsvHeader(header: string): string {
  return header
    .split('_')
    .map((word) =>
      CSV_HEADER_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
}

/**
 * Generates a CSV template with headers and an example row
 */
export function generateTemplate(columns: CSVColumn[]): string {
  const headers = columns.map((col) => col.header);
  const exampleRow = columns.map((col) => {
    if (col.exampleValue) {
      return col.exampleValue;
    }
    if (col.enumValues?.length) {
      return col.enumValues[0];
    }
    if (col.required) {
      return `Example ${col.header}`;
    }
    return '';
  });

  return Papa.unparse([headers, exampleRow]);
}

/**
 * Downloads data as a CSV file. When `columns` is provided, headers and row
 * order are derived from the column definitions (data is indexed by `col.key`).
 * Without `columns`, the object keys are used as headers in their natural order.
 */
export function downloadCSV<T extends object>(
  data: T[],
  filename: string,
  columns?: CSVColumn[],
): void {
  let csv: string;

  if (columns) {
    const headers = columns.map((col) => col.header);
    const rows = data.map((row) =>
      columns.map((col) => (row as Record<string, string>)[col.key] ?? ''),
    );
    csv = Papa.unparse([headers, ...rows]);
  } else {
    csv = Papa.unparse(data);
  }

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Downloads template CSV file
 */
export function downloadTemplate(columns: CSVColumn[], entityName: string): void {
  const template = generateTemplate(columns);
  const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${entityName}_template.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Creates a header-to-key mapping for parsing
 */
function createHeaderMapping(columns: CSVColumn[]): Map<string, string> {
  const mapping = new Map<string, string>();
  columns.forEach((col) => {
    mapping.set(col.header.toLowerCase(), col.key);
  });
  return mapping;
}

/**
 * Validates a single row against column definitions
 */
function validateRow(
  row: Record<string, string>,
  columns: CSVColumn[],
  rowIndex: number,
): CSVValidationError[] {
  const errors: CSVValidationError[] = [];

  columns.forEach((col) => {
    const value = row[col.key]?.trim() ?? '';

    if (col.required && !value) {
      errors.push({
        row: rowIndex,
        field: col.header,
        message: `${col.header} is required`,
        value,
      });
    }

    if (value && col.enumValues?.length) {
      const validValues = col.enumValues.map((v) => v.toUpperCase());

      if (col.isMultipleEnum) {
        const tokens = value
          .split(',')
          .map((token) => token.trim())
          .filter(Boolean);
        const invalidValues = tokens.filter(
          (token) => !validValues.includes(token.toUpperCase()),
        );

        if (tokens.length === 0 || invalidValues.length > 0) {
          errors.push({
            row: rowIndex,
            field: col.header,
            message: `${col.header} must be one of: ${col.enumValues.join(', ')}`,
            value,
          });
        }
      } else {
        const normalizedValue = value.toUpperCase();
        if (!validValues.includes(normalizedValue)) {
          errors.push({
            row: rowIndex,
            field: col.header,
            message: `${col.header} must be one of: ${col.enumValues.join(', ')}`,
            value,
          });
        }
      }
    }
  });

  return errors;
}

/**
 * Parses a CSV file and validates against column definitions
 */
export function parseCSV(
  file: File,
  columns: CSVColumn[],
  columnMapping?: CSVHeaderMap,
): Promise<CSVParseResult> {
  return new Promise((resolve, reject) => {
    const headerMapping = createHeaderMapping(columns);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => {
        if (columnMapping) return header.trim();

        const normalizedHeader = header.toLowerCase().trim();
        return headerMapping.get(normalizedHeader) ?? header;
      },
      complete: (results) => {
        const allErrors: CSVValidationError[] = [];
        const validRows: Record<string, string>[] = [];
        const invalidRows: {
          row: Record<string, string>;
          errors: CSVValidationError[];
        }[] = [];

        const fileRows = results.data as Record<string, string>[];
        const rows = columnMapping
          ? fileRows.map((fileRow) =>
              Object.fromEntries(
                Object.entries(columnMapping).map(([key, header]) => [
                  key,
                  fileRow[header] ?? '',
                ]),
              ),
            )
          : fileRows;

        rows.forEach((row, index) => {
          // Row index + 2 because: +1 for 0-based index, +1 for header row
          const rowNumber = index + 2;
          const rowErrors = validateRow(row, columns, rowNumber);

          if (rowErrors.length > 0) {
            allErrors.push(...rowErrors);
            invalidRows.push({ row, errors: rowErrors });
          } else {
            validRows.push(row);
          }
        });

        resolve({
          data: rows,
          errors: allErrors,
          validRows,
          invalidRows,
        });
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      },
    });
  });
}

// Enough rows to find a non-blank sample for sparsely filled columns without reading the whole file
const SAMPLE_ROW_LIMIT = 20;

export function readCSVHeaders(file: File): Promise<CSVFileHeaders> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      preview: SAMPLE_ROW_LIMIT,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        const headers = (results.meta.fields ?? []).filter(Boolean);
        const sampleValues: Record<string, string> = {};

        headers.forEach((header) => {
          const sample = results.data.map((row) => row[header]?.trim()).find(Boolean);
          if (sample) sampleValues[header] = sample;
        });

        resolve({ headers, sampleValues });
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV: ${error.message}`));
      },
    });
  });
}

/**
 * Converts failed rows back to CSV for download
 */
export function exportFailedRows(
  failedRows: { row: Record<string, string>; errors: CSVValidationError[] }[],
  columns: CSVColumn[],
  entityName: string,
): void {
  const headers = [...columns.map((col) => col.header), 'Errors'];
  const rows = failedRows.map(({ row, errors }) => {
    const rowData = columns.map((col) => row[col.key] ?? '');
    const errorMessages = errors.map((e) => e.message).join('; ');
    return [...rowData, errorMessages];
  });

  const csv = Papa.unparse([headers, ...rows]);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${entityName}_failed_rows.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const MAX_FILE_SIZE_IN_MB = 5;

export function validateFileSize(file: File, maxSizeMB = MAX_FILE_SIZE_IN_MB): boolean {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
}
